import React, { useState } from 'react';
import { useUnits } from 'core/contexts/units';
import { useTranslation } from 'react-i18next';
import { Field, useFormState } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { Add, Check, Close, ExpandLess, ExpandMore } from '@mui/icons-material';
import { Alert, Box, Button, Collapse, FormHelperText, Grid2, IconButton, InputLabel, styled, Typography } from '@mui/material';
import { NumberInput, Switch } from '@utriper/core-ui';
import UnitLinkForm from 'components/UnitLinkForm';

const Label = styled(InputLabel)({
    fontSize: '16px',
    fontWeight: 400,
    color: '#131313',
});

const UnitLinkFormFields = ({ form, showCapacity = false }) => {
    const { t } = useTranslation();
    const translate = (key, options) => t(`unit_link_form_fields.${key}`, options);
    const { values, errors } = useFormState();
    const { units } = useUnits();
    const [openForm, setOpenForm] = useState(false);
    const handleAddUnit = ({ unitId, capacity, minPerBooking, maxPerBooking, isActive, type }) => {
        const prevValues = values?.units ?? [];

        const newUnit = {
            unitId,
            capacity: capacity !== undefined ? parseInt(capacity) : null,
            minPerBooking: minPerBooking !== undefined ? parseInt(minPerBooking) : null,
            maxPerBooking: maxPerBooking !== undefined ? parseInt(maxPerBooking) : null,
            isActive,
            type,
        };

        form.change('units', [...prevValues, newUnit]);
        setOpenForm(false);
    };

    return (
        <React.Fragment>
            {showCapacity && values?.capacity && (
                <Alert sx={{ width: '100%' }} severity="info">
                    {translate('capacity_alert', { capacity: values?.capacity })}
                </Alert>
            )}
            <FieldArray name="units">
                {({ fields }) => (
                    <Box sx={{ maxHeight: 320, overflowY: 'auto', width: '100%', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        {fields.map((name, index) => {
                            const unit = values?.units[index];
                            if (!unit?.unitId) return '';

                            const isExpanded = unit?.expanded || false;
                            const unitData = units.find((u) => u.id === unit.unitId);

                            const handleExpandForm = () => form.change(`${name}.expanded`, !isExpanded);

                            const handleActive = (event) => form.change(`${name}.isActive`, event?.target?.checked);

                            return (
                                <Box
                                    sx={{
                                        width: '100%',
                                        padding: '12px',
                                        border: 'solid 1px #13131340',
                                        borderRadius: '4px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        boxSizing: 'border-box',
                                        gap: '16px',
                                    }}
                                >
                                    {/* Header */}
                                    <Grid2 container size={12} alignItems="center">
                                        <Grid2 size={8}>
                                            <Typography sx={{ color: '#131313', fontSize: '18px', fontWeight: 400 }}>{unitData?.label}</Typography>
                                        </Grid2>
                                        <Grid2 container size={4} justifyContent="flex-end">
                                            <IconButton onClick={handleExpandForm}>{isExpanded ? <ExpandLess /> : <ExpandMore />}</IconButton>
                                        </Grid2>
                                    </Grid2>

                                    {/* Form */}
                                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                        <Grid2 container size={12} direction="column" spacing={3} px={1}>
                                            {/* Capacity */}
                                            <Grid2 container size={12} spacing={2}>
                                                <Grid2 container size={8} alignItems="center">
                                                    <Label>{translate('capacity_label')}</Label>
                                                </Grid2>
                                                <Grid2 size={4}>
                                                    <Field name={`${name}.capacity`} component={NumberInput} placeholder={translate('capacity_placeholder')} />
                                                </Grid2>
                                            </Grid2>

                                            {/* Min Per Booking */}
                                            <Grid2 container size={12} spacing={2}>
                                                <Grid2 container size={8} alignItems="center">
                                                    <Label>{translate('min_per_booking_label')}</Label>
                                                </Grid2>
                                                <Grid2 size={4}>
                                                    <Field name={`${name}.minPerBooking`} component={NumberInput} placeholder={translate('min_per_booking_placeholder')} />
                                                </Grid2>
                                            </Grid2>

                                            {/* Max Per Booking */}
                                            <Grid2 container size={12} spacing={2}>
                                                <Grid2 container size={8} alignItems="center">
                                                    <Label>{translate('max_per_booking_label')}</Label>
                                                </Grid2>
                                                <Grid2 size={4}>
                                                    <Field name={`${name}.maxPerBooking`} component={NumberInput} placeholder={translate('max_per_booking_placeholder')} />
                                                </Grid2>
                                            </Grid2>

                                            {/* Is Active */}
                                            <Grid2
                                                container
                                                size={12}
                                                sx={{
                                                    '& .MuiSwitch-switchBase': {
                                                        '&.Mui-checked': {
                                                            color: '#fff',
                                                            '& + .MuiSwitch-track': {
                                                                backgroundColor: '#137DC5',
                                                                opacity: 1,
                                                            },
                                                        },
                                                    },
                                                }}
                                            >
                                                <Field type="checkbox" name={`${name}.isActive`} component={Switch} label={translate('is_active_label')} input={{ checked: unit?.isActive, onChange: handleActive }} />
                                            </Grid2>
                                        </Grid2>
                                    </Collapse>
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </FieldArray>

            <Button sx={{ color: 'var(--color-utriper-blue)', textTransform: 'none', width: 'fit-content' }} variant="text" startIcon={<Add />} onClick={() => setOpenForm(true)}>
                {translate('add_button')}
            </Button>

            {showCapacity && values?.capacity && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '100%' }}>
                    {(values?.units ?? [])
                        .filter((u) => u?.unitId)
                        .map((unit, idx) => {
                            const unitData = units.find((u) => u.id === unit.unitId);
                            const slotCapacity = Number(values?.capacity);
                            const hasCapacity = unit?.capacity != null && unit?.capacity !== '';
                            const unitCap = Number(unit?.capacity);
                            const exceeds = hasCapacity && unitCap > slotCapacity;
                            const capacityText = hasCapacity ? unitCap : translate('unlimited_capacity');
                            return (
                                <Grid2 key={`${unit.unitId}-${idx}`} container spacing={1} alignItems="center">
                                    <Typography sx={{ color: '#131313', fontSize: '16px' }}>{translate('unit_capacity_row', { unit: unitData?.label, capacity: capacityText, slotCapacity })}</Typography>
                                    {exceeds ? <Close color="error" /> : <Check color="success" />}
                                </Grid2>
                            );
                        })}
                </Box>
            )}

            {errors?.unitCapacity && <FormHelperText error>{errors.unitCapacity}</FormHelperText>}

            <UnitLinkForm
                onSubmit={handleAddUnit}
                submitting={false}
                open={openForm}
                onClose={() => {
                    setOpenForm(false);
                }}
                unitsCreated={values?.units || []}
            />
        </React.Fragment>
    );
};

export default UnitLinkFormFields;
